import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Heart } from 'lucide-react';
import { copy } from '@/copy';

const Navigation = ({ variant = 'default' }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  const isActive = (path) => location.pathname === path;
  
  // Don't show navigation on checkpoint pages (distraction-free)
  if (location.pathname.startsWith('/checkpoint/')) {
    return null;
  }

  const linkClass = (path) => `
    text-sm transition-colors duration-200
    ${isActive(path) 
      ? 'text-[#1D2D44] font-medium' 
      : 'text-[#424E59] hover:text-[#1D2D44]'
    }
  `;

  // Minimal navigation for logged-in users
  if (user) {
    return (
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E5E5E5] sticky top-0 z-50">
        <nav className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-1">
            <span className="font-serif text-lg text-[#1D2D44]">
              Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
            </span>
          </Link>
          
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className={linkClass('/dashboard')} data-testid="nav-home-link">
              {copy.nav.home}
            </Link>
            <Link to="/reflections" className={linkClass('/reflections')} data-testid="nav-reflections-link">
              {copy.nav.myReflections}
            </Link>
            <Link to="/profile" className={linkClass('/profile')} data-testid="nav-profile-link">
              Profile
            </Link>
            <Link to="/about" className={linkClass('/about')} data-testid="nav-about-link">
              {copy.nav.about}
            </Link>
            {user.is_admin && (
              <Link
                to="/admin"
                className={linkClass('/admin')}
                data-testid="nav-admin-link"
                style={{ color: '#C9A84C' }}
              >
                Admin
              </Link>
            )}
          </div>
        </nav>
      </header>
    );
  }

  // Navigation for non-logged-in users (landing, about pages)
  return (
    <header className={`sticky top-0 z-50 ${variant === 'transparent' ? 'bg-transparent' : 'bg-white/80 backdrop-blur-sm border-b border-[#E5E5E5]'}`}>
      <nav className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1">
          <span className={`font-serif text-lg ${variant === 'transparent' ? 'text-white' : 'text-[#1D2D44]'}`}>
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
          </span>
        </Link>
        
        <div className="flex items-center gap-6">
          <Link 
            to="/about" 
            className={`text-sm transition-colors duration-200 ${
              variant === 'transparent' 
                ? 'text-white/80 hover:text-white' 
                : 'text-[#424E59] hover:text-[#1D2D44]'
            } ${isActive('/about') ? 'font-medium' : ''}`}
            data-testid="nav-about-link"
          >
            {copy.nav.about}
          </Link>
          <Link 
            to="/login" 
            className={`text-sm transition-colors duration-200 ${
              variant === 'transparent' 
                ? 'text-white/80 hover:text-white' 
                : 'text-[#424E59] hover:text-[#1D2D44]'
            }`}
            data-testid="nav-signin-link"
          >
            {copy.nav.signIn}
          </Link>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
